/**
 * A client mirror of the server's enforced-thumbnail-style rule: the three conditions of
 * `resolveEnforcedStyleVerdict()` in
 * `backend/core-api/src/main/java/com/cubeia/nano/core/api/central/CentralImagesConfig.java`
 * (with `EnforcedStyleVerdict.java`), in the order the server checks them, so a document failing
 * several conditions is reported the same way on both sides.
 *
 * The verdict carries the reason and the numbers the rule objected to, never a sentence: the copy
 * belongs to the view, which translates it. The wording it renders still has to match
 * `enforcedStyleWarning()` / `ignoredStyleWarning()` in
 * `backend/core/src/main/java/com/cubeia/nano/services/json/JsonDocumentValidationService.java`,
 * so the operator can match this against the warning a save returns.
 *
 * As-you-edit feedback only. The server's verdict at save time remains authoritative: when the two
 * disagree the server is right, and this file is what needs correcting.
 */
import { StyleSize, ThumbnailStyle } from "./centralImagesDocument";

export type EnforcedStyleUnusableReason = "no-size-entry" | "non-positive-size" | "no-url-template";

/** Only the non-positive case has numbers to report, so only that member carries them and the view cannot render a sentence with a hole in it. */
export type EnforcedStyleUnusable =
  | { kind: "unusable"; style: ThumbnailStyle; reason: "no-size-entry" }
  | { kind: "unusable"; style: ThumbnailStyle; reason: "non-positive-size"; width: number; height: number }
  | { kind: "unusable"; style: ThumbnailStyle; reason: "no-url-template" };

export type EnforcedStyleVerdict =
  | { kind: "enforced"; style: ThumbnailStyle; width: number; height: number }
  | { kind: "not-configured" }
  | EnforcedStyleUnusable;

/** What the rule needs to reach a verdict, so a caller can ask about a state it has not written yet. */
export interface EnforcedStyleInput {
  defaultStyle?: ThumbnailStyle;
  size?: StyleSize;
  urlTemplate?: string;
}

export function resolveEnforcedStyleVerdict(input: EnforcedStyleInput): EnforcedStyleVerdict {
  const style = input.defaultStyle;
  if (style === undefined) {
    return { kind: "not-configured" };
  }

  const size = input.size;
  if (size === undefined) {
    return { kind: "unusable", style, reason: "no-size-entry" };
  }

  if (size.width <= 0 || size.height <= 0) {
    return { kind: "unusable", style, reason: "non-positive-size", width: size.width, height: size.height };
  }

  const urlTemplate = input.urlTemplate;
  if (urlTemplate === undefined || urlTemplate.trim() === "") {
    return { kind: "unusable", style, reason: "no-url-template" };
  }

  return { kind: "enforced", style, width: size.width, height: size.height };
}
